import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './AR7CheatSheet.css';

function AR7CheatSheet() {
  const [isCfdExplainerOpen, setIsCfdExplainerOpen] = useState(false);

  useEffect(() => {
    document.title = 'AR7 cheat sheet | electricitybills.uk';
  }, []);

  return (
    <div className="ar7-container">
      <div className="ar7-content">
        <h1>AR7 cheat sheet</h1>

        <p>
          <i>Allocation Round 7 is the next auction in the UK's Contracts for Difference scheme. The output of the auction is a fixed "strike price". Contracted generators will get paid this price for the next 20 years.</i>
        </p>

        <div className="dropdown-section">
          <button
            className="dropdown-header"
            onClick={() => setIsCfdExplainerOpen(!isCfdExplainerOpen)}
          >
            <span>How Contracts for Difference work</span>
            <span className="dropdown-arrow">{isCfdExplainerOpen ? '▲' : '▼'}</span>
          </button>
          {isCfdExplainerOpen && (
            <div className="dropdown-content">
              <p>
                Contracts for Difference are the UK's flagship mechanism for funding renewables. They guarantee renewable generators a fixed "strike" price for their electricity. This helps project developers to raise financing.
              </p>
              <ul>
                <li>
                  When the market price is <strong>lower</strong> than the strike price, consumers top up the generator through their bills.
                </li>
                <li>
                  When the market price is <strong>higher</strong> than the strike price, generators refund consumers on their bills.
                </li>
                <li>
                  The strike price gets inflation-adjusted every year.
                </li>
              </ul>
              <img src="/cfd_svg.svg" alt="How Contracts for Difference work" className="cfd-diagram" />
            </div>
          )}
        </div>

        <h2>Pricing summary</h2>
        <ul>
          <li>
            AR7 aims to contract a lot of offshore wind. This contract will be locked in for
            20 years, so the price is important.
          </li>
          <li>
            The current maximum auction cap (<strong>£113 / MWh</strong>) is considerably above the average
            wholesale electricity price.
          </li>
        </ul>

        <img src="/ar7_imgs/wholesale_price_chart.png" alt="Wholesale price chart" />

        <h2>Pricing history</h2>
        <p>Most of AR7 is about buying offshore wind. In the past:</p>
        <ul>
          <li>AR3 and AR4 yielded good prices for offshore wind</li>
          <li>AR5 failed to procure any capacity, and AR6 was significantly more expensive</li>
        </ul>

        <img src="/ar7_imgs/offshore_wind_auction_history.png" alt="Offshore wind auction history" className="img-70" />

        <h2>Effect on energy bills</h2>
        <p>The resulting strike price of AR7 will directly impact electricity bills. This impact depends on (1) the speed of electrification, (2) the speed of transmission buildout, and (3) global gas prices.
        </p>

        <img src="/ar7_imgs/ar7_pricing.png" alt="AR7 pricing chart" className="img-70" />

        <ul>
          <li>
            AR7 has the potential to be the biggest CfD Allocation Round ever, so volumes should
            be chosen carefully.
          </li>
          <li>
            Prices will be locked in for 20 years, so this is a generational decision for the UK.
          </li>
          <li>
            Since CfDs are a fixed price contract, they have the benefit of hedging against future energy crises. 
          </li>
          <li>
            If prices are high, we should probably not purchase large volumes on autopilot.
            <ul>
              <li>
                Instead, we might reduce volumes to increase competition (the auction is pay-as-clear),
                or take targeted action to ease short run supply chain constraints (AR8 could be brought forward
                in this case).
              </li>
            </ul>
          </li>
          <li>
            We have already contracted <strong>35GW</strong> of CfDs, of which <strong>25GW</strong> are still to come online before 2030.
          </li>
          <li>
            For reference: in 2030, the volume blended strike price of existing active CfDs will be <strong>£93 / MWh</strong> (£2025).
          </li>
        </ul>

        <p>
          Note that the cost of new build gas is likely to be significantly higher than these prices (likely <a href="https://www.notion.so/ben-james/Cost-of-new-build-gas-in-UK-29682e8cf66880028714de7c60ee1b5d?source=copy_link" target="_blank" rel="noopener noreferrer">at least £130 / MWh</a>). However, this gas capacity will need to be built irrespective
          of new CfD rounds, so it does not compete directly. New gas will be procured by the Capacity
          Market to supply low-wind winter weeks and fulfil the <a href="https://www.neso.energy/document/346791/download" target="_blank" rel="noopener noreferrer">required</a> 35GW of unabated gas in Clean
          Power 2030 - not to displace wind generation.
        </p>

        <h2>Market price dilution</h2>
        <p>
          CfDs are resolving an increasing amount of UK electricity supply off-market. We must
          carefully monitor how this impacts system operation.
        </p>

        <img src="/ar7_imgs/volume_by_scheme.png" alt="CfD volume by scheme" className="img-70" />

        <p>
          By 2030, over half of electricity supply will flow through CfDs or the Renewables
          Obligation (before AR7). This dilutes the wholesale price signal, which will often
          clear near zero. This may cause unexpected behaviour.
        </p>
        <ul>
          <li>
            For example, the wholesale price might clear at <strong>£10 / MWh</strong>, but the effective CfD cost on bills is <strong>£90 / MWh</strong>. If Europe's electricity price is <strong>£50 / MWh</strong>, we
            will export large amounts of energy into France, subsidised by UK consumers.
          </li>
          <li>
            Two areas that hold great promise for lowering bills are battery storage and
            consumer flexibility. Both depend on an intact wholesale price signal. 
            If the wholesale price does not reflect the true cost of energy in the system, batteries and flexibility may take actions that align less closely with system needs.
          </li>
        </ul>

        <h2>Summary</h2>
        <ul>
          <li>
            CfDs at AR3 and AR4 prices (<strong>£50 - 60 / MWh</strong>) offer good value to consumers and a valuable hedge against future energy crises.
          </li>
          <li>
            CfDs clearing near the AR7 maximum price (<strong>£85 - £113 / MWh</strong>) are likely to raise bills and may distort system behaviour.
          </li>
        </ul>

        <p className="footer-text">
          By <a href="https://benjames.io" target="_blank" rel="noopener noreferrer">Ben James</a>. Talk to me about AR7: <a href="mailto:ben@benjames.io">ben@benjames.io</a>
        </p>

        <p className="disclaimer-text">
          <i>All prices in £2025</i>
        </p>

        <Link to="/" className="back-button">
          ← Back to Bill Breakdown
        </Link>
      </div>
    </div>
  );
}

export default AR7CheatSheet;
